/**
 * Battery calculation utilities for different device types
 * Based on reference React Native app formulas
 * 
 * Device types:
 * - SIGMAWIT: 1500-4000mV range, 25mV per percentage point
 * - KBeacon: 2000-3300mV range
 * - ELA: 2800-3200mV range (400mV range)
 */

/**
 * Get battery percentage from millivolts based on device type
 * @param {number} batteryLevel - Battery level in millivolts (mV) or percentage
 * @param {string} deviceType - Device type: 'sigmawit', 'kbeacon', 'ela', or undefined
 * @returns {number|null} Battery percentage (0-100) or null if invalid
 */
export const getBatteryPercentage = (batteryLevel, deviceType) => {
  if (!batteryLevel || batteryLevel <= 0 || isNaN(batteryLevel)) {
    return null;
  }

  // Normalize device type to lowercase
  // IMPORTANT: deviceType comes from Supabase sensors.device_type (set by React Native app)
  // If deviceType is null/undefined, we cannot accurately calculate - return null instead of guessing
  const normalizedType = deviceType ? deviceType.toLowerCase().trim() : null;
  
  // If deviceType is not provided from Supabase, we cannot accurately calculate battery percentage
  // This matches React Native behavior - device_type must be set correctly in the database by React Native app
  if (!normalizedType) {
    console.warn('⚠️ Battery calculation: device_type is missing from Supabase. Cannot calculate accurate battery percentage.');
    return null; // Return null instead of using wrong formula
  }

  // SIGMAWIT devices: Battery is stored in mV (1500-4000mV range) in the database
  // Formula: 0% = 1500mV, 100% = 4000mV (25mV per percentage point)
  // This includes T201 and BLogm2 models which are SIGMAWIT variants
  if (normalizedType === 'sigmawit' || normalizedType === 't201' || normalizedType === 'blogm2') {
    // Check if value is already in mV range (1500-4000)
    if (batteryLevel >= 1500 && batteryLevel <= 4000) {
      // Convert mV to percentage: (mV - 1500) / 25
      const percentage = (batteryLevel - 1500) / 25;
      return Math.min(100, Math.max(0, Math.round(percentage)));
    }
    // If value is already in percentage range (0-100), return as-is
    if (batteryLevel >= 0 && batteryLevel <= 100) {
      return Math.min(100, Math.max(0, Math.round(batteryLevel)));
    }
    // Fallback: assume it's mV and convert
    const percentage = (batteryLevel - 1500) / 25;
    return Math.min(100, Math.max(0, Math.round(percentage)));
  }

  // ELA devices: Battery can be sent as percentage (0-100) or voltage in mV (1500-4000mV)
  // When sent as voltage: 2800mV = 0%, 3200mV = 100% (400mV range)
  // When sent as percentage: use directly (0xF1 when < 15% or from Android bridge)
  // IMPORTANT: This matches React Native SensorCard.tsx logic exactly
  if (normalizedType === 'ela') {
    // Check if value is already in percentage range (0-100)
    // ELA can send battery as percentage via 0xF1 (when < 15%) or from Android KBeaconBridge
    if (batteryLevel >= 0 && batteryLevel <= 100) {
      return Math.min(100, Math.max(0, Math.round(batteryLevel)));
    }
    
    // Check if value is in ELA voltage range (1500-4000mV, typical 2800-3200mV)
    // ELA voltage range: 2800mV = 0%, 3200mV = 100% (400mV range)
    // This matches the Android KBeaconModule conversion: (voltage - 2800) * 100 / 400
    if (batteryLevel >= 1500 && batteryLevel <= 4000) {
      // Convert voltage to percentage: (voltage - 2800) / 400 * 100
      const percentage = ((batteryLevel - 2800) / 400) * 100;
      return Math.min(100, Math.max(0, Math.round(percentage)));
    }
    
    // Fallback: try standard ELA conversion even if outside typical range
    const percentage = ((batteryLevel - 2800) / 400) * 100;
    return Math.min(100, Math.max(0, Math.round(percentage)));
  }

  // KBeacon devices: Convert mV to approximate percentage
  // 3300mV = 100%, 2000mV = 0%
  // Formula: (batteryLevel - 2000) / (3300 - 2000) * 100
  // Handle all KBeacon variants: "kbeacon", "kbpro", "ksensor"
  if (normalizedType === 'kbeacon' || normalizedType === 'kbpro' || normalizedType === 'ksensor') {
    const percentage = ((batteryLevel - 2000) / (3300 - 2000)) * 100;
    return Math.min(100, Math.max(0, Math.round(percentage)));
  }

  // If device type is not recognized, return null instead of guessing
  // This ensures we don't show incorrect battery percentages
  // The React Native app should set device_type correctly when adding sensors
  console.warn(`⚠️ Battery calculation: Unknown device type "${deviceType}". Cannot calculate battery percentage.`);
  return null;
};

/**
 * Get battery voltage range information for a device type
 * @param {string} deviceType - Device type: 'sigmawit', 'kbeacon', 'ela', or undefined
 * @returns {object} Object with min, max, and range information
 */
export const getBatteryVoltageRange = (deviceType) => {
  const normalizedType = deviceType ? deviceType.toLowerCase() : null;

  switch (normalizedType) {
    case 'sigmawit':
    case 't201':
    case 'blogm2':
      return {
        min: 1500,
        max: 4000,
        range: 2500,
        formula: '0% = 1500mV, 100% = 4000mV (25mV per %)',
      };
    case 'ela':
      return {
        min: 2800,
        max: 3200,
        range: 400,
        formula: '0% = 2800mV, 100% = 3200mV (400mV range)',
      };
    case 'kbeacon':
    case 'kbpro':
    case 'ksensor':
      return {
        min: 2000,
        max: 3300,
        range: 1300,
        formula: '0% = 2000mV, 100% = 3300mV',
      };
    default:
      return {
        min: 2000,
        max: 3300,
        range: 1300,
        formula: '0% = 2000mV, 100% = 3300mV (KBeacon default)',
      };
  }
};

/**
 * Format battery display with percentage and voltage
 * @param {number} batteryLevel - Battery level in millivolts (mV)
 * @param {string} deviceType - Device type: 'sigmawit', 'kbeacon', 'ela', or undefined
 * @param {boolean} showVoltage - Whether to show voltage alongside percentage
 * @returns {string} Formatted battery string (e.g., "85% (3400mV)")
 */
export const formatBatteryDisplay = (batteryLevel, deviceType, showVoltage = true) => {
  if (!batteryLevel) return 'N/A';
  
  const percentage = getBatteryPercentage(batteryLevel, deviceType);
  if (percentage === null) return 'N/A';
  
  if (showVoltage && batteryLevel >= 1000) {
    // Only show voltage if it's in a reasonable mV range (not already a percentage)
    if (batteryLevel >= 1500 && batteryLevel <= 4000) {
      return `${percentage}% (${Math.round(batteryLevel)}mV)`;
    }
  }
  
  return `${percentage}%`;
};

/**
 * Get battery color based on percentage
 * @param {number} percentage - Battery percentage (0-100)
 * @returns {string} Color code for battery status
 */
export const getBatteryColor = (percentage) => {
  if (percentage === null || percentage === undefined) return '#8d8d8d'; // Gray for unknown
  if (percentage >= 70) return '#34C759'; // Green - Good
  if (percentage >= 30) return '#FF9500'; // Orange - Medium
  return '#FF453A'; // Red - Low
};

